import React, { useState, useEffect } from 'react';
import { Plus, X, Calculator, Save, Eye, AlertCircle, Percent } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { formatPrice } from '../../utils/discountUtils';
import AdminLayout from './AdminLayout';
import { useAdminAuth } from '../../contexts/AdminAuthContext';

const GlobalDiscountSettings = () => {
  const { toast } = useToast();
  const { adminAuthToken } = useAdminAuth();
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  // Load current discount settings
  useEffect(() => {
    loadDiscountSettings();
  }, []);

  const loadDiscountSettings = async () => {
    try {
      setLoading(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/settings/quantity-discounts`);
      
      if (!response.ok) {
        throw new Error('Failed to load discount settings');
      }
      
      const data = await response.json();
      if (data.success) {
        setDiscounts(data.discounts);
      }
    } catch (error) {
      console.error('Error loading discount settings:', error);
      toast({
        title: "Errore",
        description: "Impossibile caricare le impostazioni sconti",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddDiscount = () => {
    const newDiscount = {
      minQuantity: discounts.length > 0 ? Math.max(...discounts.map(d => d.maxQuantity || d.minQuantity)) + 1 : 2,
      maxQuantity: null,
      discountPercentage: 5.0,
      description: `Sconto per ${discounts.length > 0 ? Math.max(...discounts.map(d => d.maxQuantity || d.minQuantity)) + 1 : 2}+ bottiglie`,
      isActive: true
    };
    
    setDiscounts([...discounts, newDiscount]);
  };

  const handleRemoveDiscount = (index) => {
    setDiscounts(discounts.filter((_, i) => i !== index));
  };

  const handleDiscountChange = (index, field, value) => {
    const updated = discounts.map((discount, i) => {
      if (i === index) {
        const newDiscount = { ...discount };
        
        if (field === 'minQuantity' || field === 'maxQuantity') {
          newDiscount[field] = value ? parseInt(value) || 1 : null;
        } else if (field === 'discountPercentage') {
          newDiscount[field] = parseFloat(value) || 0;
        } else if (field === 'isActive') {
          newDiscount[field] = value;
        } else {
          newDiscount[field] = value;
        }
        
        return newDiscount;
      }
      return discount;
    });
    
    setDiscounts(updated);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Debug: Check if token is available
      console.log('Admin auth token:', adminAuthToken ? 'Present' : 'Missing');
      console.log('Token length:', adminAuthToken?.length);
      
      // Validate data before sending
      const validationErrors = [];
      
      discounts.forEach((discount, index) => {
        if (discount.minQuantity <= 0) {
          validationErrors.push(`Sconto ${index + 1}: la quantità minima deve essere maggiore di 0`);
        }
        if (discount.discountPercentage < 0 || discount.discountPercentage > 100) {
          validationErrors.push(`Sconto ${index + 1}: la percentuale deve essere tra 0 e 100`);
        }
        if (discount.maxQuantity && discount.maxQuantity <= discount.minQuantity) {
          validationErrors.push(`Sconto ${index + 1}: la quantità massima deve essere maggiore della minima`);
        }
      });

      if (validationErrors.length > 0) {
        toast({
          title: "Errori di validazione",
          description: validationErrors.join('\n'),
          variant: "destructive"
        });
        return;
      }

      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      if (!adminAuthToken) {
        throw new Error('Token di autenticazione non disponibile');
      }
      
      const response = await fetch(`${backendUrl}/api/settings/quantity-discounts`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminAuthToken}`
        },
        body: JSON.stringify({ discounts })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save discount settings');
      }

      const data = await response.json();
      if (data.success) {
        setDiscounts(data.discounts);
        toast({
          title: "Successo",
          description: data.message || "Impostazioni sconti salvate con successo"
        });
      }
    } catch (error) {
      console.error('Error saving discount settings:', error);
      toast({
        title: "Errore",
        description: error.message || "Impossibile salvare le impostazioni sconti",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    try {
      setPreviewing(true);
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/settings/quantity-discounts/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ discounts })
      });

      if (!response.ok) {
        throw new Error('Failed to generate preview');
      }

      const data = await response.json();
      if (data.success) {
        setPreviewData(data);
      }
    } catch (error) {
      console.error('Error generating preview:', error);
      toast({
        title: "Errore",
        description: "Impossibile generare l'anteprima",
        variant: "destructive"
      });
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
          <p className="text-center mt-2 text-gray-600">Caricamento impostazioni sconti...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Sconti Quantità Globali</h2>
          <p className="text-gray-600 mt-1">
            Gestisci gli sconti applicati automaticamente in base alla quantità totale nel carrello
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handlePreview}
            disabled={previewing}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <Eye className="w-4 h-4 mr-2" />
            {previewing ? 'Generando...' : 'Anteprima'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Salvando...' : 'Salva'}
          </button>
        </div>
      </div>

      {/* Information Box */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 mr-3" />
          <div className="text-sm text-amber-800">
            <p className="font-medium mb-1">Come funzionano gli sconti quantità:</p>
            <ul className="list-disc ml-4 space-y-1">
              <li>Gli sconti si applicano al totale del carrello in base alla quantità totale di prodotti</li>
              <li>Lo sconto viene applicato solo al prezzo base dei prodotti (esclusi bottiglie e design)</li>
              <li>Le fasce non devono sovrapporsi</li>
              <li>Usare "null" per quantità massima illimitata (es. 12+)</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Discount Configuration */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Configurazione Sconti</h3>
          <button
            onClick={handleAddDiscount}
            className="flex items-center px-3 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            <Plus className="w-4 h-4 mr-1" />
            Aggiungi Sconto
          </button>
        </div>

        <div className="space-y-4">
          {discounts.map((discount, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-start mb-3">
                <h4 className="font-medium text-gray-900">Fascia Sconto #{index + 1}</h4>
                <button
                  onClick={() => handleRemoveDiscount(index)}
                  className="text-red-600 hover:text-red-800"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantità Minima
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={discount.minQuantity}
                    onChange={(e) => handleDiscountChange(index, 'minQuantity', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantità Massima
                  </label>
                  <input
                    type="number"
                    min={discount.minQuantity + 1}
                    value={discount.maxQuantity || ''}
                    onChange={(e) => handleDiscountChange(index, 'maxQuantity', e.target.value)}
                    placeholder="Illimitato"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sconto %
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={discount.discountPercentage}
                      onChange={(e) => handleDiscountChange(index, 'discountPercentage', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500 pr-8"
                    />
                    <Percent className="absolute right-3 top-2.5 w-4 h-4 text-gray-400" />
                  </div>
                </div>

                <div className="flex items-end">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={discount.isActive}
                      onChange={(e) => handleDiscountChange(index, 'isActive', e.target.checked)}
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Attivo</span>
                  </label>
                </div>
              </div>

              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrizione
                </label>
                <input
                  type="text"
                  value={discount.description || ''}
                  onChange={(e) => handleDiscountChange(index, 'description', e.target.value)}
                  placeholder="Descrizione dello sconto"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                />
              </div>

              {/* Quick Preview for this tier */}
              <div className="mt-3 p-2 bg-gray-50 rounded text-sm text-gray-600">
                Fascia: {discount.minQuantity} - {discount.maxQuantity || '∞'} bottiglie → 
                <span className="font-medium text-green-600 ml-1">{discount.discountPercentage}% di sconto</span>
              </div>
            </div>
          ))}

          {discounts.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Calculator className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Nessuno sconto configurato</p>
              <p className="text-sm">Clicca "Aggiungi Sconto" per iniziare</p>
            </div>
          )}
        </div>
      </div>

      {/* Preview Results */}
      {previewData && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Anteprima Sconti</h3>
          <p className="text-sm text-gray-600 mb-4">
            Simulazione con prezzo base prodotto: {formatPrice(previewData.sampleBasePrice)}
          </p>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantità</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Totale Originale</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sconto %</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sconto €</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Totale Finale</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Risparmio</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {previewData.previews.map((preview, index) => (
                  <tr key={index} className={preview.discountPercentage > 0 ? 'bg-green-50' : ''}>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {preview.quantity} {preview.quantity === 1 ? 'bottiglia' : 'bottiglie'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatPrice(preview.originalTotal)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {preview.discountPercentage > 0 ? `${preview.discountPercentage}%` : '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {preview.discountAmount > 0 ? `${formatPrice(preview.discountAmount)}` : '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatPrice(preview.finalTotal)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                      {preview.savings > 0 ? `${formatPrice(preview.savings)}` : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      </div>
    </AdminLayout>
  );
};

export default GlobalDiscountSettings;